import React, { useEffect, useState } from 'react';
import { 
  X, 
  Eye, 
  Download, 
  ExternalLink, 
  ShieldCheck, 
  Loader2,
  Share2
} from 'lucide-react';
import { Receipt } from '../types';
import { UserProfile } from '../../../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { 
  formatPhone, 
  buildFullAddress, 
  getReceiptTypeLabel, 
  generateQrCodeDataUrl 
} from '../utils/receiptPdfUtils';

interface ReceiptPdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  receipt: Receipt;
  user: Partial<UserProfile>;
  onOpenInNewTab?: () => void;
  onDownloadPDF?: () => void;
  isGenerating?: boolean;
  generationStep?: string;
}

export default function ReceiptPdfPreviewModal({
  isOpen,
  onClose,
  receipt,
  user,
  onOpenInNewTab,
  onDownloadPDF,
  isGenerating = false,
  generationStep = ''
}: ReceiptPdfPreviewModalProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && receipt.pixCopiaCola) {
      generateQrCodeDataUrl(receipt.pixCopiaCola).then(url => {
        setQrCodeDataUrl(url);
      });
    } else {
      setQrCodeDataUrl('');
    }
  }, [isOpen, receipt.pixCopiaCola]);

  if (!isOpen) return null;

  const todayStr = new Date(receipt.issuedAt || Date.now()).toLocaleDateString('pt-BR');
  const title = user?.businessName || user?.ownerName || 'Prestador de Serviços';
  const phonePart = user?.phone ? `WhatsApp: ${formatPhone(user.phone)}` : '';
  const addressPart = buildFullAddress(user);
  const contactLine = [phonePart, addressPart].filter(Boolean).join(' • ');

  const clientName = receipt.clientName || 'Cliente';
  const logoSrc = user?.logoUrl || user?.logoBase64;
  const typeInfo = getReceiptTypeLabel(receipt.receiptType);

  const rawPixKey = (user?.pixKey || '').toString().trim();
  const rawPixType = (user?.pixKeyType || 'PIX').toString().trim().toUpperCase();

  const totalValue = Number(receipt.totalServiceValue || receipt.amountPaid || 0);
  const amountPaid = Number(receipt.amountPaid || 0);
  const remainingBalance = Math.max(0, totalValue - amountPaid);

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/40 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto font-sans">
      <div className="relative w-full max-w-4xl bg-white border border-zinc-200 rounded-3xl shadow-2xl flex flex-col my-auto max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
              <Eye className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-black text-zinc-900 uppercase tracking-wider">
                  Prévia do Recibo de Pagamento
                </h2>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black uppercase px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Pro • Com sua Marca
                </span>
              </div>
              <p className="text-[11px] text-zinc-500">
                Visualização exata do documento que o seu cliente receberá.
              </p>
            </div>
          </div>
          
          <button 
            type="button"
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 rounded-full transition-colors cursor-pointer"
            title="Fechar Prévia"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content - Scrollable PDF Sheet (Folha A4 Exata) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-zinc-100/70">
          <div className="max-w-2xl mx-auto bg-white text-zinc-900 rounded-2xl shadow-xl p-6 sm:p-10 border border-zinc-200 relative">
            
            {/* Header: Logo + Business Info */}
            <div className="flex justify-between items-start border-b border-zinc-200 pb-6 mb-6">
              <div className="text-left space-y-1.5 max-w-[65%]">
                {logoSrc ? (
                  <img 
                    src={logoSrc} 
                    alt="Logo" 
                    className="w-14 h-14 rounded-full object-cover border border-zinc-200 mb-2" 
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-900 font-black text-xs mb-2">
                    R MAX
                  </div>
                )}
                <h3 className="text-lg sm:text-xl font-black text-zinc-950 uppercase tracking-tight leading-tight">
                  {title}
                </h3>
                {contactLine && (
                  <p className="text-xs text-zinc-500 font-medium leading-relaxed">
                    {contactLine}
                  </p>
                )}
              </div>

              <div className="text-right space-y-2 shrink-0">
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">NÚMERO</p>
                  <p className="text-base sm:text-lg font-black text-zinc-950">{receipt.receiptNumber}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">DATA</p>
                  <p className="text-xs sm:text-sm font-black text-zinc-800">{todayStr}</p>
                </div>
              </div>
            </div>

            {/* Recipient */}
            <div className="mb-6 text-left">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-0.5">Recibo para:</p>
              <p className="text-xl sm:text-2xl font-black text-zinc-950">
                {clientName}
                {receipt.clientDocument && (
                  <span className="text-xs font-semibold text-zinc-500 block mt-0.5">
                    CPF/CNPJ: {receipt.clientDocument}
                  </span>
                )}
              </p>
            </div>

            {/* Table */}
            <div className="mb-6 overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-zinc-950">
                    <th className="py-2.5 text-xs font-black text-zinc-500 uppercase tracking-wider">
                      Descrição do Serviço Realizado
                    </th>
                    <th className="py-2.5 text-xs font-black text-zinc-500 uppercase tracking-wider text-right">
                      TOTAL
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  <tr>
                    <td className="py-4 text-left">
                      <div className="text-base font-bold text-zinc-950">{receipt.description}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Modalidade: <strong>{typeInfo.label}</strong> • Forma de Pagamento: <strong>{receipt.paymentMethod.toUpperCase()}</strong>
                      </div>
                    </td>
                    <td className="py-4 text-right align-top font-black text-zinc-950 text-base">
                      {formatCurrencyBRL(totalValue)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Total Quitado em Destaque */}
            <div className="border-t-2 border-zinc-200 py-4 text-right">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">
                VALOR TOTAL PAGO NESTE RECIBO
              </p>
              <h2 className="text-2xl sm:text-3xl font-black text-emerald-700 tracking-tight">
                {formatCurrencyBRL(amountPaid)}
              </h2>
              {remainingBalance > 0 ? (
                <p className="text-xs font-bold text-amber-700 mt-1">
                  Saldo Restante a Pagar: {formatCurrencyBRL(remainingBalance)}
                </p>
              ) : (
                <p className="text-xs font-bold text-emerald-600 mt-1 uppercase tracking-wide">
                  ✓ Pagamento 100% Concluído
                </p>
              )}
            </div>

            {/* Terms & PIX */}
            <div className="border-t border-zinc-100 pt-4 space-y-2 text-left">
              {receipt.warrantyPeriod && receipt.warrantyPeriod > 0 && (
                <p className="text-xs font-bold text-zinc-700">
                  🛡️ Garantia: {receipt.warrantyPeriod} {receipt.warrantyUnit || 'dias'} ({receipt.warrantyTerms || 'Garantia de mão de obra e materiais conforme combinado.'})
                </p>
              )}

              {(rawPixKey || receipt.pixCopiaCola) && (
                <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="space-y-1 flex-1">
                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-wider mb-0.5">
                      Dados para Pagamento via PIX:
                    </p>
                    {rawPixKey && (
                      <p className="text-xs sm:text-sm font-black text-zinc-900">
                        Chave {rawPixType}: <span className="text-blue-700">{rawPixKey}</span>
                      </p>
                    )}
                    {receipt.pixCopiaCola && (
                      <p className="text-[10px] font-mono text-zinc-600 break-all line-clamp-2">
                        {receipt.pixCopiaCola}
                      </p>
                    )}
                  </div>

                  {qrCodeDataUrl && (
                    <div className="p-1.5 bg-white border border-zinc-200 rounded-lg shrink-0 text-center">
                      <img src={qrCodeDataUrl} alt="QR Code PIX" className="w-16 h-16 mx-auto" />
                      <span className="text-[8px] font-bold text-zinc-500 uppercase block mt-0.5">QR Code PIX</span>
                    </div>
                  )}
                </div>
              )}

              <p className="text-[11px] text-zinc-400 pt-2 border-t border-zinc-100">
                Assinado por: <strong className="text-zinc-700">{title}</strong>
              </p>
            </div>

            {/* Footer com Marca */}
            <div className="mt-4 pt-2 text-center">
              <span className="text-[9px] text-zinc-400 uppercase tracking-widest font-bold">
                ✓ Documento Oficial Personalizado com sua Marca
              </span>
            </div>

          </div>
        </div>

        {/* Modal Actions Footer */}
        <div className="p-4 sm:p-5 border-t border-zinc-200 bg-zinc-50 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-zinc-600 text-center sm:text-left">
            <span className="text-emerald-700 font-bold">✓ PDF oficial personalizado com sua marca</span>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            {onOpenInNewTab && (
              <button
                type="button"
                onClick={onOpenInNewTab}
                disabled={isGenerating}
                className="flex-1 sm:flex-none py-3 px-4 bg-white hover:bg-zinc-100 text-zinc-800 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer border border-zinc-300 active:scale-95 shadow-xs"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Visualizar PDF</span>
              </button>
            )}

            {onDownloadPDF && (
              <button
                type="button"
                onClick={onDownloadPDF}
                disabled={isGenerating}
                className="flex-1 sm:flex-none py-3 px-5 bg-brand hover:bg-brand-dark text-white font-black rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg shadow-brand/20 cursor-pointer active:scale-95"
              >
                {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span>{isGenerating ? (generationStep || 'Gerando...') : 'Baixar / Compartilhar PDF'}</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
